import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAdminAuth } from "../AdminAuthContext";

export default function AdminLogin() {
  const { user, login } = useAdminAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    const result = await login(form.email.trim(), form.password);
    setBusy(false);
    if (result.ok) navigate("/admin", { replace: true });
    else setError(result.message);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0E1A13] px-6" data-testid="admin-login-page">
      <div className="w-full max-w-sm">
        <p className="font-serif text-2xl text-cream">OneHarvestCo.</p>
        <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.3em] text-gold/80">
          Growth Desk · Internal
        </p>

        <form onSubmit={submit} className="mt-10 space-y-5" data-testid="admin-login-form">
          <div className="space-y-2">
            <label htmlFor="admin-email" className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream/50">
              Email
            </label>
            <input
              id="admin-email"
              type="email"
              required
              autoComplete="username"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="admin-email-input"
              className="w-full rounded-lg border border-cream/15 bg-cream/5 px-4 py-3 text-sm text-cream outline-none transition-colors duration-200 placeholder:text-cream/30 focus:border-gold/60"
              placeholder="you@ohcbenefits.com"
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="admin-password" className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream/50">
              Password
            </label>
            <input
              id="admin-password"
              type="password"
              required
              autoComplete="current-password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="admin-password-input"
              className="w-full rounded-lg border border-cream/15 bg-cream/5 px-4 py-3 text-sm text-cream outline-none transition-colors duration-200 placeholder:text-cream/30 focus:border-gold/60"
              placeholder="••••••••"
            />
          </div>

          {error ? (
            <p className="text-sm text-terra" data-testid="admin-login-error">
              {error}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-cream px-6 py-3 text-sm font-medium text-forest transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <p className="mt-8 text-xs leading-relaxed text-cream/35">
          Authorized users only. Activity in this console is logged against your account.
        </p>
      </div>
    </div>
  );
}
